import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  Phone, Mail, MapPin, Clock, Send, MessageSquare, CheckCircle, AlertCircle
} from 'lucide-react';

// --- ANIMATION VARIANTS ---

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  margin-top: 80px;
  min-height: calc(100vh - 80px);
  background-color: #F8FAFC; /* Slate-50 */
  padding: 80px 24px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 64px;

  h1 {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 16px;
    letter-spacing: -0.02em;
  }

  p {
    color: #64748b;
    font-size: 1.1rem;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
  }
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

// --- LEFT SIDE: INFO CARDS ---

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoCard = styled(motion.div)`
  background: white;
  padding: 24px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  gap: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
    border-color: #6366f1;

    .icon-box {
      background: #4f46e5;
      color: white;
    }
  }

  .icon-box {
    width: 48px;
    height: 48px;
    background: #f1f5f9;
    color: #64748b;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  .content {
    h3 {
      font-size: 1.1rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 8px;
    }
    p {
      color: #64748b;
      font-size: 0.95rem;
      line-height: 1.5;
      margin: 0;
    }
    .highlight {
      color: #4f46e5;
      font-weight: 500;
    }
  }
`;

// --- RIGHT SIDE: FORM ---

const FormCard = styled(motion.div)`
  background: white;
  padding: 40px;
  border-radius: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  font-size: 1rem;
  color: #0f172a;
  background: #f8fafc;
  transition: all 0.2s;

  &:focus {
    outline: none;
    background: white;
    border-color: #6366f1;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  font-size: 1rem;
  color: #0f172a;
  background: #f8fafc;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    background: white;
    border-color: #6366f1;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #0f172a;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s;

  &:hover {
    background: #334155;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Alert = styled(motion.div)`
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;

  &.success {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #dcfce7;
  }
`;

// --- COMPONENT ---

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState(null); // null, 'success', 'error'

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate API call
    console.log(formData);
    setStatus('success');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });

    // Clear status after 5s
    setTimeout(() => setStatus(null), 5000);
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <Header initial="hidden" animate="visible" variants={fadeInUp}>
          <h1>Get in Touch</h1>
          <p>
            Have a question about a shipment or need a custom logistics solution?
            Our team is ready to help you 24/7.
          </p>
        </Header>

        <Grid
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* LEFT: INFO */}
          <InfoSection>
            <InfoCard variants={fadeInUp}>
              <div className="icon-box"><Phone size={24} /></div>
              <div className="content">
                <h3>Call Us</h3>
                <p className="highlight">+91 7029141680</p>
                <p style={{ fontSize: '0.85rem', marginTop: '4px' }}>Mon-Sat, 9am - 8pm</p>
              </div>
            </InfoCard>

            <InfoCard variants={fadeInUp}>
              <div className="icon-box"><Mail size={24} /></div>
              <div className="content">
                <h3>Email Us</h3>
                <p>support@sddlogistics.com</p>
                <p>sales@sddlogistics.com</p>
              </div>
            </InfoCard>

            <InfoCard variants={fadeInUp}>
              <div className="icon-box"><MapPin size={24} /></div>
              <div className="content">
                <h3>Head Office</h3>
                <p>Grand Trunk Road, Barddhaman,<br />West Bengal, India - 713101</p>
              </div>
            </InfoCard>

            <InfoCard variants={fadeInUp}>
              <div className="icon-box"><Clock size={24} /></div>
              <div className="content">
                <h3>Operations</h3>
                <p>24/7 Transport Services</p>
                <p>Office: 9:00 AM - 6:00 PM</p>
              </div>
            </InfoCard>
          </InfoSection>

          {/* RIGHT: FORM */}
          <FormCard variants={fadeInUp}>
            <h2><MessageSquare size={24} color="#6366f1" /> Send a Message</h2>

            {status === 'success' && (
              <Alert
                className="success"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
              >
                <CheckCircle size={20} /> Message sent successfully! We'll reply shortly.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <FormGroup>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Mobile Number"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Subject</Label>
                <Input
                  type="text"
                  name="subject"
                  placeholder="How can we help?"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Message</Label>
                <TextArea
                  name="message"
                  placeholder="Tell us more about your requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <SubmitButton type="submit">
                <Send size={18} /> Send Message
              </SubmitButton>
            </form>
          </FormCard>
        </Grid>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Contact;